import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { useCart } from "../context/CartContext";
import { handleError } from "../utils/errorHandler";
import {
  formatPrice,
  formatPixPrice,
  formatInstallment,
} from "../utils/formatters";
import EmptyState from "../components/common/EmptyState";
import Loading from "../components/ui/Loading";
import SEOHead from "../components/common/SEOHead";
import OptimizedImage from "../components/common/OptimizedImage";
import { generateCollectionStructuredData } from "../utils/seo";
import { useProductsQuery } from "../hooks/useProductsQuery";
import pixIcon from "../image/pix.png";
import "../styles/ProductCatalog.css";
import whatsapp from "../image/whatsapp.png";

import useAuth from "../context/useAuth";
import {
  generateContextualWhatsAppLink,
  isBusinessHours,
  WHATSAPP_CONTEXT,
  trackWhatsAppClick,
} from "../utils/whatsapp";
import { getProductUrl } from "../utils/productUrl";
import { isProductOutOfStock } from "../utils/productStock";
import {
  CATALOG_IMAGE_SIZES,
  getLcpPreloadAttrs,
} from "../utils/cloudinaryImage";
import { loadToastifyCss } from "../utils/loadToastifyCss";
import "../styles/productStockBadge.css";

function extractProductBrands(products) {
  if (!Array.isArray(products) || products.length === 0) return [];
  return [
    ...new Set(
      products
        .map((product) => product.brand)
        .filter(Boolean)
        .map((brand) =>
          typeof brand === "string" ? brand.trim() : String(brand || "").trim()
        )
        .filter(Boolean)
    ),
  ];
}

function mergeBrandLists(previous, incoming) {
  const current = Array.isArray(previous) ? previous : [];
  const merged = [...new Set([...current, ...(incoming || [])])].sort((a, b) =>
    a.localeCompare(b, "pt", { sensitivity: "base" })
  );
  if (
    merged.length === current.length &&
    merged.every((brand, index) => brand === current[index])
  ) {
    return previous || current;
  }
  return merged;
}

const ProductCatalog = () => {
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const { addItemToCart, cartItems } = useCart();

  // ✅ Filtros e ordenação
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [tvModel, setTvModel] = useState("");
  const [boardCode, setBoardCode] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const filterOptionsRequestRef = useRef(null);

  const { user } = useAuth();
  const location = useLocation();

  const productsQuery = useProductsQuery({
    page,
    limit: 12,
    brand: selectedBrand,
    category: selectedCategory,
    tvModel,
    boardCode,
    minPrice,
    maxPrice,
    sortBy,
    sortOrder,
  });
  const products = productsQuery.products;
  const pagination = productsQuery.pagination;
  // Só bloqueia a grade no primeiro load (sem dados); refetch mantém a lista
  const loading = productsQuery.isLoading;
  const loadError = productsQuery.error;
  const catalogReady = !loading || products.length > 0;

  // ✅ Resetar estados quando navegar para a página inicial (clicar na logo)
  useEffect(() => {
    // Se estamos na rota "/" e não há query params, resetar tudo
    if (location.pathname === "/" && !location.search) {
      // Verificar se há filtros aplicados ou não está na primeira página
      const hasFilters =
        selectedBrand ||
        selectedCategory ||
        tvModel ||
        boardCode ||
        minPrice ||
        maxPrice ||
        sortBy !== "createdAt" ||
        sortOrder !== "desc";

      // Se há filtros aplicados ou não está na página 1, resetar
      if (hasFilters || page !== 1) {
        setSelectedBrand("");
        setSelectedCategory("");
        setTvModel("");
        setBoardCode("");
        setMinPrice("");
        setMaxPrice("");
        setSortBy("createdAt");
        setSortOrder("desc");
        setPage(1);
        setShowFilters(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]); // Executa quando a rota muda

  // Toast de boas-vindas só depois do load/LCP, para não competir com a thread principal
  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;
    let idleId;
    let timeoutId;

    const showWelcome = () => {
      if (cancelled) return;

      loadToastifyCss().then(() => {
        if (cancelled) return;

        const nomeCliente = user?.name || "visitante";
        const isPT = navigator.language.startsWith("pt");
        const mensagemTexto = isPT
          ? `Olá ${nomeCliente}! Seja bem-vindo(a) à Emperc — placas e fontes de TV testadas em bancada, para técnicos.`
          : `Hello ${nomeCliente}! Welcome to Emperc — bench-tested TV boards and power supplies for technicians.`;

        toast.info(mensagemTexto, {
          position: "top-right",
          autoClose: 7000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      });
    };

    const schedule = () => {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(showWelcome, { timeout: 4000 });
      } else {
        timeoutId = window.setTimeout(showWelcome, 2000);
      }
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", schedule);
      if (idleId != null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [user]);

  // ✅ Construir link do WhatsApp usando useMemo para evitar recálculos desnecessários
  // Isso garante que a mensagem seja recalculada quando user, cartItems ou filtros mudarem
  const whatsappLink = useMemo(() => {
    const nomeCliente = user?.name || "visitante";
    const isPT = navigator.language.startsWith("pt");
    const currentUrl =
      typeof window !== "undefined" ? window.location.href : "";

    return generateContextualWhatsAppLink({
      context: WHATSAPP_CONTEXT.CATALOG,
      messageOptions: {
        nomeCliente,
        isPT,
        cartItems,
        currentUrl,
        contextoAdicional: isPT
          ? "Estou no *catálogo* e preciso de ajuda para encontrar a peça certa (compatibilidade / estoque)."
          : "I'm browsing the *catalog* and need help finding the right part (compatibility / stock).",
        filtros: {
          selectedBrand,
          selectedCategory,
          minPrice,
          maxPrice,
        },
        categories,
      },
    });
  }, [
    user,
    cartItems,
    selectedBrand,
    selectedCategory,
    minPrice,
    maxPrice,
    categories,
  ]);

  // Categorias/marcas não entram no LCP: filtros começam fechados.
  // Busca imediata se o usuário abrir os filtros; senão, depois do catálogo + idle.
  useEffect(() => {
    let idleId;
    let timeoutId;

    const loadFilterOptions = () => {
      if (filterOptionsRequestRef.current) return filterOptionsRequestRef.current;

      const base = process.env.REACT_APP_BACKEND_URL;

      filterOptionsRequestRef.current = Promise.all([
        (async () => {
          try {
            const response = await fetch(`${base}/api/categories`);
            const data = await response.json();
            if (Array.isArray(data)) setCategories(data);
          } catch (error) {
            handleError(error, { context: "ProductCatalog.fetchCategories" });
          }
        })(),
        (async () => {
          try {
            const response = await fetch(`${base}/api/products/filters/brands`);
            if (!response.ok) return;
            const json = await response.json();
            const list = json.data?.brands;
            if (Array.isArray(list)) {
              setBrands((prev) => mergeBrandLists(prev, list));
            }
          } catch (error) {
            handleError(error, {
              context: "ProductCatalog.fetchFilterOptions",
              showToast: false,
            });
          }
        })(),
      ]).catch(() => {
        filterOptionsRequestRef.current = null;
      });

      return filterOptionsRequestRef.current;
    };

    if (showFilters) {
      loadFilterOptions();
      return undefined;
    }

    if (!catalogReady) {
      return undefined;
    }

    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(loadFilterOptions, { timeout: 3500 });
      } else {
        timeoutId = window.setTimeout(loadFilterOptions, 1500);
      }
    };

    schedule();

    return () => {
      if (idleId != null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [showFilters, catalogReady]);

  // Fallback: mescla marcas da página atual se /filters/brands ainda não carregou
  const pageBrandKey = useMemo(
    () => extractProductBrands(products).join("\u0001"),
    [products]
  );

  useEffect(() => {
    if (!pageBrandKey) return;
    const fromPage = pageBrandKey.split("\u0001");
    setBrands((prev) => mergeBrandLists(prev, fromPage));
  }, [pageBrandKey]);

  // ✅ Scroll para o topo dos produtos ao mudar de página
  useEffect(() => {
    if (page > 1) {
      const productGrid = document.querySelector(".product-grid");
      if (productGrid) {
        productGrid.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [page]);

  const handleAddToCart = async (product) => {
    if (isProductOutOfStock(product)) {
      toast.error("Este produto está sem estoque.");
      return;
    }
    await addItemToCart({
      id: product._id,
      name: product.name,
      price: product.price,
      image: product.image || (product.images?.[0] ?? undefined),
      quantity: 1,
    });
  };

  // ✅ Função para mudar de página
  const handlePageChange = useCallback(
    (newPage) => {
      const totalPages = pagination?.totalPages || 1;
      const currentPage = pagination?.currentPage || page;

      // Validar se a nova página é válida e diferente da atual
      if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
        setPage(newPage);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [pagination, page]
  );

  // ✅ Calcular páginas visíveis baseado no tamanho da tela
  const getVisiblePages = useMemo(() => {
    if (!pagination) return [];

    const totalPages = pagination.totalPages;
    const currentPage = pagination.currentPage || page;

    // Detectar tamanho da tela de forma segura
    let maxVisible = 7;
    if (typeof window !== "undefined") {
      if (window.innerWidth < 768) {
        maxVisible = 3;
      } else if (window.innerWidth < 1024) {
        maxVisible = 5;
      }
    }

    const pages = [];
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Primeira página
    if (startPage > 1) {
      pages.push({ type: "number", value: 1 });
      if (startPage > 2) {
        pages.push({ type: "ellipsis" });
      }
    }

    // Páginas visíveis
    for (let i = startPage; i <= endPage; i++) {
      pages.push({ type: "number", value: i, isActive: i === currentPage });
    }

    // Última página
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push({ type: "ellipsis" });
      }
      pages.push({ type: "number", value: totalPages });
    }

    return pages;
  }, [pagination, page]);

  const handleFilterChange = () => {
    setPage(1); // Reset para primeira página ao mudar filtros
  };

  const clearFilters = () => {
    setSelectedBrand("");
    setSelectedCategory("");
    setTvModel("");
    setBoardCode("");
    setMinPrice("");
    setMaxPrice("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setPage(1);
  };

  // Mostra loading enquanto carrega pela primeira vez
  if (loading && products.length === 0) {
    return (
      <div className="product-catalog">
        <Loading fullScreen message="Carregando produtos..." />
      </div>
    );
  }

  if (!loading && loadError && products.length === 0) {
    return (
      <div className="product-catalog">
        <EmptyState
          icon="⚠️"
          title="Erro ao carregar produtos"
          message="Não foi possível buscar o catálogo. Verifique a conexão e tente novamente."
          actionLabel="Tentar novamente"
          onAction={() => productsQuery.refetch()}
        />
      </div>
    );
  }

  // Só mostra mensagem de vazio se já tentou carregar e não tem produtos
  if (!loading && (!products || products.length === 0)) {
    return (
      <div className="product-catalog">
        <EmptyState
          icon="🔍"
          title="Nenhum produto encontrado"
          message="Não encontramos produtos com os filtros selecionados. Tente ajustar os filtros ou voltar mais tarde."
        />
      </div>
    );
  }

  const catalogDescription = `Confira ${
    pagination?.totalItems || "nossas"
  } placas e peças de TV testadas em bancada. Catálogo Emperc para técnicos — garantia de funcionamento.`;
  const catalogUrl = typeof window !== "undefined" ? window.location.href : "";
  const collectionStructuredData =
    products.length > 0
      ? generateCollectionStructuredData(
          products.slice(0, 10),
          "Placas e peças de TV"
        )
      : null;
  const lcpImageUrl =
    page === 1
      ? products[0]?.image || products[0]?.images?.[0] || ""
      : "";
  const lcpPreload = getLcpPreloadAttrs(lcpImageUrl, {
    sizes: CATALOG_IMAGE_SIZES,
  });

  return (
    <main className="product-catalog" id="main-content" role="main">
      <SEOHead
        title="Catálogo de placas de TV"
        description={catalogDescription}
        url={catalogUrl}
        type="website"
        keywords="placas de TV, placa principal, fonte TV, PCI, peças para técnicos, placas usadas testadas, Emperc"
        structuredData={collectionStructuredData}
      />
      {lcpPreload ? (
        <Helmet>
          {lcpPreload.imageSrcSet ? (
            <link
              rel="preload"
              as="image"
              href={lcpPreload.href}
              imageSrcSet={lcpPreload.imageSrcSet}
              imageSizes={lcpPreload.imageSizes}
              fetchPriority="high"
            />
          ) : (
            <link
              rel="preload"
              as="image"
              href={lcpPreload.href}
              fetchPriority="high"
            />
          )}
        </Helmet>
      ) : null}

      {/* ✅ Seção de Navegação e Informações do Catálogo */}
      <div className="bg-[#4a7291] px-4 py-1 md:px-6 md:py-2">
        {/* Título, Contador e Botão de Filtros */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 m-0">
              Placas e peças de TV
            </h1>
            {pagination && (
              <p className="text-sm md:text-base text-white m-0 opacity-90">
                {pagination.totalItems}{" "}
                {pagination.totalItems === 1
                  ? "produto disponível"
                  : "produtos disponíveis"}
              </p>
            )}
          </div>

          {/* Botão de Filtros */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-primary hover:bg-primary-dark text-white border-none rounded-md cursor-pointer text-sm md:text-base font-medium transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
            aria-label={showFilters ? "Fechar filtros" : "Abrir filtros"}
            tabIndex={0}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {showFilters ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
            <span>
              {showFilters ? "Fechar Filtros" : "Filtros e Ordenação"}
            </span>
          </button>
        </div>
      </div>

      {/* ✅ Filtros e ordenação */}
      {showFilters && (
        <div className="bg-gray-50 p-5 md:p-6 rounded-lg mb-6 border border-gray-200 shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4">
            {/* Filtro por categoria */}
            <div>
              <label className="block mb-2 font-medium text-sm text-gray-700">
                Categoria
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  handleFilterChange();
                }}
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                aria-label="Filtrar por categoria"
              >
                <option value="">Todas as categorias</option>
                {categories.map((category) => (
                  <option
                    key={category._id}
                    value={category.slug || category._id}
                  >
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por marca */}
            <div>
              <label className="block mb-2 font-medium text-sm text-gray-700">
                Marca
              </label>
              <select
                value={selectedBrand}
                onChange={(e) => {
                  setSelectedBrand(e.target.value);
                  handleFilterChange();
                }}
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                aria-label="Filtrar por marca"
              >
                <option value="">Todas as marcas</option>
                {brands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </div>

            {/* Modelo de TV */}
            <div>
              <label className="block mb-2 font-medium text-sm text-gray-700">
                Modelo da TV
              </label>
              <input
                type="text"
                value={tvModel}
                onChange={(e) => {
                  setTvModel(e.target.value);
                  handleFilterChange();
                }}
                placeholder="Ex.: UN55JU6700"
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                aria-label="Filtrar por modelo da TV"
              />
            </div>

            {/* Código da placa */}
            <div>
              <label className="block mb-2 font-medium text-sm text-gray-700">
                Código da placa
              </label>
              <input
                type="text"
                value={boardCode}
                onChange={(e) => {
                  setBoardCode(e.target.value);
                  handleFilterChange();
                }}
                placeholder="Ex.: EAX68102603"
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                aria-label="Filtrar por código da placa"
              />
            </div>

            {/* Filtro por preço mínimo */}
            <div>
              <label className="block mb-2 font-medium text-sm text-gray-700">
                Preço Mínimo (R$)
              </label>
              <input
                type="number"
                value={minPrice}
                onChange={(e) => {
                  setMinPrice(e.target.value);
                  handleFilterChange();
                }}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                aria-label="Preço mínimo"
              />
            </div>

            {/* Filtro por preço máximo */}
            <div>
              <label className="block mb-2 font-medium text-sm text-gray-700">
                Preço Máximo (R$)
              </label>
              <input
                type="number"
                value={maxPrice}
                onChange={(e) => {
                  setMaxPrice(e.target.value);
                  handleFilterChange();
                }}
                placeholder="9999.99"
                min="0"
                step="0.01"
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                aria-label="Preço máximo"
              />
            </div>

            {/* Ordenação */}
            <div>
              <label className="block mb-2 font-medium text-sm text-gray-700">
                Ordenar por
              </label>
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split("-");
                  setSortBy(field);
                  setSortOrder(order);
                  handleFilterChange();
                }}
                className="w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                aria-label="Ordenar produtos"
              >
                <option value="createdAt-desc">Mais Recentes</option>
                <option value="price-asc">Menor Preço</option>
                <option value="price-desc">Maior Preço</option>
                <option value="name-asc">Nome (A-Z)</option>
                <option value="name-desc">Nome (Z-A)</option>
                <option value="rating-desc">Melhor Avaliação</option>
              </select>
            </div>
          </div>

          {/* Botão limpar filtros */}
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-white text-[#0052a3] border-2 border-[#0052a3] rounded-md cursor-pointer text-sm font-medium hover:bg-[#0052a3] hover:text-white transition-all duration-200 active:scale-95 underline"
            aria-label="Limpar todos os filtros"
            tabIndex={0}
          >
            Limpar Filtros
          </button>
        </div>
      )}

      <div className="product-grid">
        {products.map((product, index) => {
          // Primeira imagem recebe priority para LCP
          const isFirstImage = index === 0;
          const installmentPrice = (product.price / 3).toFixed(2);
          const pixPrice = (product.price * 0.9).toFixed(2);
          const imageUrl =
            product.image ||
            (product.images?.[0] ??
              "https://via.placeholder.com/250x200?text=Sem+Imagem");
          const productUrl = getProductUrl(product);
          const outOfStock = isProductOutOfStock(product);

          return (
            <div key={product._id} className="product-card" data-index={index}>
              <Link to={productUrl} className="product-stock-image-wrap">
                <OptimizedImage
                  src={imageUrl}
                  alt={product.name}
                  width={250}
                  height={200}
                  className="product-image"
                  priority={isFirstImage}
                  objectFit="contain"
                  productId={product._id}
                  imageId={product.imageId}
                  originalUrl={imageUrl}
                  sizes={CATALOG_IMAGE_SIZES}
                />
                {outOfStock ? (
                  <span className="product-out-of-stock-badge">Sem estoque</span>
                ) : null}
              </Link>
              <div className="product-info">
                <h2 className="product-name">
                  <Link to={productUrl}>{product.name}</Link>
                </h2>
                {/* Preço total (sem desconto) - DESTAQUE PRINCIPAL */}
                <p className="product-price">
                  {formatPrice(product.price)} à vista
                </p>
                {/* Parcelamento */}
                <p className="product-installment">
                  ou 3x de {formatInstallment(product.price)} s/ juros
                </p>
                {/* Preço PIX com desconto de 10% - abaixo */}
                <p className="product-pix">
                  <img
                    src={pixIcon}
                    alt="Pix"
                    className="pix-icon"
                    width="30"
                    height="30"
                  />{" "}
                  {formatPixPrice(product.price)} no Pix
                </p>
              </div>
              <button
                onClick={() => handleAddToCart(product)}
                className="add-to-cart-btn"
                aria-label={
                  outOfStock
                    ? `${product.name} sem estoque`
                    : `Adicionar ${product.name} ao carrinho`
                }
                disabled={outOfStock}
                title={
                  outOfStock
                    ? "Produto sem estoque"
                    : `Adicionar ${product.name} ao carrinho`
                }
              >
                {outOfStock ? "Sem estoque" : "Comprar Agora"}
              </button>
            </div>
          );
        })}
      </div>

      {/* ✅ Componente de Paginação */}
      {pagination && pagination.totalPages > 1 && (
        <div className="pagination-container">
          <nav className="pagination" aria-label="Navegação de páginas">
            <button
              onClick={() => {
                const prevPage = (pagination?.currentPage || page) - 1;
                if (prevPage >= 1) {
                  handlePageChange(prevPage);
                }
              }}
              disabled={(pagination?.currentPage || page) <= 1 || loading}
              className="pagination-btn pagination-prev"
              aria-label="Página anterior"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M10 12L6 8L10 4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="pagination-btn-text">Anterior</span>
            </button>

            <div className="pagination-numbers">
              {getVisiblePages.map((pageItem, index) => {
                if (pageItem.type === "ellipsis") {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="pagination-ellipsis"
                    >
                      ...
                    </span>
                  );
                }

                return (
                  <button
                    key={`page-${pageItem.value}`}
                    onClick={() => handlePageChange(pageItem.value)}
                    className={`pagination-number ${
                      pageItem.isActive ? "active" : ""
                    }`}
                    aria-label={`Página ${pageItem.value}`}
                    aria-current={pageItem.isActive ? "page" : undefined}
                  >
                    {pageItem.value}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => {
                const nextPage = (pagination?.currentPage || page) + 1;
                const totalPages = pagination?.totalPages || 1;
                if (nextPage <= totalPages) {
                  handlePageChange(nextPage);
                }
              }}
              disabled={
                (pagination?.currentPage || page) >=
                  (pagination?.totalPages || 1) || loading
              }
              className="pagination-btn pagination-next"
              aria-label="Próxima página"
            >
              <span className="pagination-btn-text">Próxima</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M6 4L10 8L6 12"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </nav>
        </div>
      )}

      {/* ✅ Barra de Ações Flutuante - Apenas WhatsApp */}
      <div className="icon-action-bar">
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Fale conosco no WhatsApp${
            cartItems.length > 0
              ? ` - ${cartItems.length} item(ns) no carrinho`
              : ""
          }${isBusinessHours() ? " - Online agora" : " - Fora do horário"}`}
          title={`Fale conosco no WhatsApp${
            cartItems.length > 0
              ? ` - ${cartItems.length} item(ns) no carrinho`
              : ""
          }${
            isBusinessHours()
              ? " - Estamos online agora!"
              : " - Responderemos assim que possível"
          }`}
          className="flat-icon-btn whatsapp-float-btn"
          onClick={() => {
            trackWhatsAppClick("whatsapp_clicked", {
              page: "catalog",
              hasCartItems: cartItems.length > 0,
              cartItemsCount: cartItems.length,
              isBusinessHours: isBusinessHours(),
              hasFilters: !!(
                selectedBrand ||
                selectedCategory ||
                minPrice ||
                maxPrice
              ),
            });
          }}
        >
          <img src={whatsapp} alt="WhatsApp" className="whatsapp" />
        </a>
      </div>
    </main>
  );
};

export default ProductCatalog;
